import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { CssBaseline, Link, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import api from "../../services/api";
import {
  applyBranding,
  getCachedBranding,
  resolveBrandingUrl
} from "../../utils/branding";

const defaultSlides = [
  {
    imageUrl:
      "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1400&q=80",
    title: "Atencion organizada",
    description: "Conserva cada conversacion, responsable y seguimiento en contexto."
  },
  {
    imageUrl:
      "https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&w=1400&q=80",
    title: "Equipos conectados",
    description: "Administra conversaciones, areas y automatizaciones sin perder visibilidad."
  },
  {
    imageUrl:
      "https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=1400&q=80",
    title: "Seguimiento claro",
    description: "Haz que cada oportunidad avance con recordatorios y secuencias."
  }
];

export const parseLoginSlides = value => {
  if (!value) return defaultSlides;

  try {
    const slides = typeof value === "string" ? JSON.parse(value) : value;
    if (!Array.isArray(slides)) return defaultSlides;

    const normalized = slides
      .map(slide => ({
        imageUrl: String(slide?.imageUrl || "").trim(),
        title: String(slide?.title || "").trim(),
        description: String(slide?.description || "").trim()
      }))
      .filter(slide => slide.imageUrl);

    return normalized.length ? normalized : defaultSlides;
  } catch {
    return defaultSlides;
  }
};

export const parseLoginLinks = branding => {
  try {
    const links = JSON.parse(branding?.loginLinks || "[]");
    if (Array.isArray(links)) {
      return links
        .map(link => ({
          label: String(link?.label || "").trim(),
          url: String(link?.url || "").trim()
        }))
        .filter(link => link.label && link.url);
    }
  } catch {
    // Keep compatibility with the previous two-link settings below.
  }

  return [
    {
      label: branding?.loginPrimaryLinkLabel,
      url: branding?.loginPrimaryLinkUrl
    },
    {
      label: branding?.loginSecondaryLinkLabel,
      url: branding?.loginSecondaryLinkUrl
    }
  ].filter(link => link.label && link.url);
};

const isExternalUrl = url => /^https?:\/\//i.test(String(url || ""));

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: "100vh",
    display: "grid",
    gridTemplateColumns: "minmax(360px, 46%) minmax(0, 54%)",
    backgroundColor: "#f6f7fb",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      gridTemplateRows: "auto 1fr"
    }
  },
  formSide: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4),
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(248,250,252,0.96) 100%)",
    [theme.breakpoints.down("sm")]: {
      minHeight: "auto",
      padding: theme.spacing(3, 2, 5)
    }
  },
  formShell: {
    width: "100%",
    maxWidth: 430
  },
  logo: {
    maxWidth: 190,
    maxHeight: 64,
    objectFit: "contain",
    marginBottom: theme.spacing(5),
    [theme.breakpoints.down("sm")]: {
      maxWidth: 146,
      marginBottom: theme.spacing(3)
    }
  },
  eyebrow: {
    color: theme.palette.primary.main,
    fontWeight: 700,
    textTransform: "uppercase",
    fontSize: 12,
    letterSpacing: 0
  },
  title: {
    fontWeight: 800,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    color: "#111827",
    [theme.breakpoints.down("sm")]: {
      fontSize: 30,
      lineHeight: 1.16
    }
  },
  subtitle: {
    color: "#64748b",
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      fontSize: 16
    }
  },
  extraLinks: {
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    marginTop: theme.spacing(4)
  },
  visualSide: {
    position: "relative",
    minHeight: "100vh",
    overflow: "hidden",
    backgroundColor: "#111827",
    [theme.breakpoints.down("sm")]: {
      order: -1,
      minHeight: 210
    }
  },
  slideImage: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transform: "scale(1.02)"
  },
  visualOverlay: {
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(180deg, rgba(3,7,18,0.16) 0%, rgba(3,7,18,0.68) 100%)"
  },
  slideContent: {
    position: "absolute",
    left: theme.spacing(6),
    right: theme.spacing(6),
    bottom: theme.spacing(7),
    color: "#fff",
    maxWidth: 620,
    [theme.breakpoints.down("sm")]: {
      left: theme.spacing(3),
      right: theme.spacing(3),
      bottom: theme.spacing(2.5)
    }
  },
  slideTitle: {
    fontWeight: 800,
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      fontSize: 25,
      lineHeight: 1.12
    }
  },
  slideDescription: {
    color: "rgba(255,255,255,0.84)",
    maxWidth: 540,
    [theme.breakpoints.down("sm")]: {
      fontSize: 15,
      lineHeight: 1.35
    }
  },
  dots: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      marginTop: theme.spacing(1.5)
    }
  },
  dot: {
    width: 28,
    height: 4,
    border: 0,
    borderRadius: 999,
    background: "rgba(255,255,255,0.35)",
    cursor: "pointer",
    padding: 0
  },
  dotActive: {
    background: "#fff"
  }
}));

const AuthLayout = ({
  children,
  footer,
  title,
  subtitle,
  onUserCreationLoaded
}) => {
  const classes = useStyles();
  const [branding, setBranding] = useState(getCachedBranding);
  const [activeSlide, setActiveSlide] = useState(0);

  const slides = useMemo(() => parseLoginSlides(branding.loginSlides), [branding]);
  const links = useMemo(() => parseLoginLinks(branding), [branding]);
  const currentSlide = slides[activeSlide] || slides[0];
  const logoUrl = resolveBrandingUrl(branding.logoUrl) || "/logo.png";

  useEffect(() => {
    const fetchPublicSettings = async () => {
      try {
        const [{ data: userCreation }, { data: publicBranding }] = await Promise.all([
          api.get("/settings/public/userCreation"),
          api.get("/settings/public/branding")
        ]);
        onUserCreationLoaded?.(userCreation.value !== "disabled");
        setBranding(applyBranding(publicBranding));
      } catch {
        onUserCreationLoaded?.(true);
      }
    };

    fetchPublicSettings();
  }, [onUserCreationLoaded]);

  useEffect(() => {
    const listener = event => setBranding(event.detail || getCachedBranding());
    window.addEventListener("brandingChanged", listener);
    return () => window.removeEventListener("brandingChanged", listener);
  }, []);

  useEffect(() => {
    if (slides.length <= 1) return undefined;

    const timer = setInterval(() => {
      setActiveSlide(prev => (prev + 1) % slides.length);
    }, 5500);

    return () => clearInterval(timer);
  }, [slides.length]);

  useEffect(() => {
    if (activeSlide > slides.length - 1) setActiveSlide(0);
  }, [activeSlide, slides.length]);

  const renderConfiguredLink = ({ label, url }) => {
    const external = isExternalUrl(url);
    return (
      <Link
        key={`${label}-${url}`}
        color="primary"
        variant="body2"
        component={external ? "a" : RouterLink}
        href={external ? url : undefined}
        to={external ? undefined : url}
        target={external ? "_blank" : undefined}
        rel={external ? "noopener noreferrer" : undefined}
      >
        {label}
      </Link>
    );
  };

  return (
    <main className={classes.root}>
      <CssBaseline />
      <section className={classes.formSide}>
        <div className={classes.formShell}>
          <img src={logoUrl} alt={branding.siteTitle} className={classes.logo} />
          <Typography className={classes.eyebrow}>
            {branding.siteTitle || "CRM Multiagente"}
          </Typography>
          <Typography component="h1" variant="h4" className={classes.title}>
            {title || branding.loginHeadline}
          </Typography>
          <Typography variant="body1" className={classes.subtitle}>
            {subtitle || branding.loginSubtitle}
          </Typography>
          {children}
          {footer}
          <div className={classes.extraLinks}>{links.map(renderConfiguredLink)}</div>
        </div>
      </section>
      <section className={classes.visualSide}>
        <img
          src={currentSlide.imageUrl}
          alt={currentSlide.title || branding.siteTitle}
          className={classes.slideImage}
        />
        <div className={classes.visualOverlay} />
        <div className={classes.slideContent}>
          <Typography variant="h3" component="h2" className={classes.slideTitle}>
            {currentSlide.title}
          </Typography>
          <Typography variant="h6" className={classes.slideDescription}>
            {currentSlide.description}
          </Typography>
          <div className={classes.dots}>
            {slides.map((slide, index) => (
              <button
                key={`${slide.imageUrl}-${index}`}
                type="button"
                aria-label={`Slide ${index + 1}`}
                className={`${classes.dot} ${
                  index === activeSlide ? classes.dotActive : ""
                }`}
                onClick={() => setActiveSlide(index)}
              />
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default AuthLayout;
